import { Fields } from '@constants/constant';
import { EVENT_LISTING_MSG } from '@modules/dashboard/components/event-list-item/constant/event-list-item-constant';
import { NEWS_CONSTANTS } from '@modules/monetize/components/newsletters/constants/news-letter.constant';
import { PROFILE_CONSTANT } from '@modules/shared/components/profile/constant/profile-constant';
import {
  HEADER_LAYOUT_CONSTANTS,
  buildSubPageMenuOptionId,
  buildSubPagePath
} from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout.constants';
import { getSubPageSlug } from '@modules/whitelabel-cover-page/sub-pages/sub-pages.constants';
import { HeaderMenuListItem } from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout-menu.utils';

export interface HeaderMenuProfileContext {
  username: string;
  gsi4pk: string;
}

export interface HeaderMenuProfileApiData {
  userResponse?: {
    status?: boolean;
    data?: Record<string, unknown>;
    newsletter?: { display_newsletter?: boolean };
    eventDetails?: {
      display_event_calendar?: boolean;
      event_calendar_name?: string;
    };
    portfolioDetails?: Array<{
      sk?: string;
      portfolio_title?: string;
    }>;
  };
  merchandiseResponse?: {
    status?: boolean;
    is_store_connected?: boolean;
    data?: { Items?: unknown[] };
  };
  welcomeVideoResponse?: {
    status?: boolean;
    data?: Record<string, unknown>;
  };
  subPages?: Array<{
    name: string;
    url: string;
    isEnabled: boolean;
  }>;
}

export function buildHeaderMenuProfileContext(
  username: string,
  gsi4pk: string
): HeaderMenuProfileContext {
  return {
    username: username.trim(),
    gsi4pk: gsi4pk.trim()
  };
}

export function isWelcomeVideoMenuUrl(url: string): boolean {
  const normalized = url.trim();
  if (!normalized) {
    return false;
  }
  try {
    const parsed = new URL(normalized, 'https://placeholder.local');
    return (
      parsed.searchParams.get('tab') ===
      HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.WELCOME_VIDEO
    );
  } catch {
    return normalized.includes(
      `tab=${HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.WELCOME_VIDEO}`
    );
  }
}

export function isWelcomeVideoMenuActive(data: Record<string, unknown> | null | undefined): boolean {
  if (!data) {
    return false;
  }
  const mediaUrl = data['media_url'];
  const hasMedia =
    (Array.isArray(mediaUrl) && mediaUrl.length > 0) ||
    !!data['video_url'] ||
    (Array.isArray(data['media']) && data['media'].length > 0);
  if (!hasMedia) {
    return false;
  }
  if (data['admin_marked_disabled']) {
    return false;
  }
  return data['is_active'] !== false;
}

export function buildPortfolioMenuOptionId(portfolioSk: string): string {
  const portfolioId = portfolioSk.includes('#')
    ? portfolioSk.split('#')[1]
    : portfolioSk;
  return `${HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.PORTFOLIO}${portfolioId}`;
}

export function buildHeaderMenuOptionUrl(
  optionId: string,
  context: HeaderMenuProfileContext
): string {
  const { username, gsi4pk } = context;
  const ids = HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS;
  switch (optionId) {
    case ids.FEEDS:
      return username ? `/${username}` : '';
    // case ids.LINK_IN_BIO:
    //   return username ? `/bio/${username}` : '';
    case ids.STORE:
      return username && gsi4pk
        ? `/dashboard/all-merchandise/${username}&${gsi4pk}/ALL`
        : '';
    case ids.WELCOME_VIDEO:
      return username
        ? `/${username}?tab=${PROFILE_CONSTANT.WELCOME_VIDEO}`
        : '';
    case ids.EVENTS:
      return username ? `/dashboard/event-listing/${username}` : '';
    case ids.NEWSLETTERS:
      return username ? `/dashboard/newsletters-list/${username}` : '';
    default:
      if (optionId.startsWith(HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.PORTFOLIO)) {
        const portfolioId = optionId.slice(
          HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.PORTFOLIO.length
        );
        return username && portfolioId
          ? `/dashboard/portfolio/${username}/banner-view/${portfolioId}`
          : '';
      }
      if (optionId.startsWith(HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.SUB_PAGE)) {
        const subPageUrl = optionId.slice(
          HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.SUB_PAGE.length
        );
        return buildSubPagePath(username, subPageUrl);
      }
      return '';
  }
}

export function getHeaderMenuOptionLabel(
  optionId: string,
  apiData: HeaderMenuProfileApiData
): string {
  const ids = HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS;
  switch (optionId) {
    case ids.FEEDS:
      return HEADER_LAYOUT_CONSTANTS.MENU_OPTION_LABELS.FEEDS;
    // case ids.LINK_IN_BIO:
    //   return HEADER_LAYOUT_CONSTANTS.MENU_OPTION_LABELS.LINK_IN_BIO;
    case ids.STORE:
      return HEADER_LAYOUT_CONSTANTS.MENU_OPTION_LABELS.STORE;
    case ids.WELCOME_VIDEO:
      return Fields.WELCOME_VIDEO;
    case ids.EVENTS:
      return (
        apiData.userResponse?.eventDetails?.event_calendar_name ||
        EVENT_LISTING_MSG.EVENTS
      );
    case ids.NEWSLETTERS:
      return NEWS_CONSTANTS.NEWSLETTERS;
    default:
      if (optionId.startsWith(HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.PORTFOLIO)) {
        const portfolioId = optionId.slice(
          HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.PORTFOLIO.length
        );
        const portfolio = apiData.userResponse?.portfolioDetails?.find((item) => {
          const sk = item.sk ?? '';
          const id = sk.includes('#') ? sk.split('#')[1] : sk;
          return id === portfolioId;
        });
        return portfolio?.portfolio_title?.trim() ?? optionId;
      }
      if (optionId.startsWith(HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.SUB_PAGE)) {
        const subPageUrl = optionId.slice(
          HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.SUB_PAGE.length
        );
        const subPage = apiData.subPages?.find(
          (sp) => getSubPageSlug(sp.url) === getSubPageSlug(subPageUrl)
        );
        return subPage?.name?.trim() ?? getSubPageSlug(subPageUrl);
      }
      return optionId;
  }
}

export function buildHeaderMenuMultiselectOptions(
  context: HeaderMenuProfileContext,
  apiData: HeaderMenuProfileApiData
): HeaderMenuListItem[] {
  const options: HeaderMenuListItem[] = [];
  const ids = HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS;

  const addOption = (optionId: string) => {
    const url = buildHeaderMenuOptionUrl(optionId, context);
    if (!url) {
      return;
    }
    options.push({
      id: optionId,
      label: getHeaderMenuOptionLabel(optionId, apiData),
      url,
      isPredefined: true
    });
  };

  if (context.username) {
    addOption(ids.FEEDS);
    // addOption(ids.LINK_IN_BIO);
  }

  const merchandiseItems = apiData.merchandiseResponse?.data?.Items ?? [];
  if (
    apiData.merchandiseResponse?.is_store_connected &&
    merchandiseItems.length > 0
  ) {
    addOption(ids.STORE);
  }

  if (isWelcomeVideoMenuActive(apiData.welcomeVideoResponse?.data)) {
    addOption(ids.WELCOME_VIDEO);
  }

  if (apiData.userResponse?.eventDetails?.display_event_calendar) {
    addOption(ids.EVENTS);
  }

  if (apiData.userResponse?.newsletter?.display_newsletter) {
    addOption(ids.NEWSLETTERS);
  }

  apiData.userResponse?.portfolioDetails?.forEach((portfolio) => {
    const title = portfolio.portfolio_title?.trim();
    const sk = portfolio.sk?.trim();
    if (!title || !sk) {
      return;
    }
    const optionId = buildPortfolioMenuOptionId(sk);
    addOption(optionId);
  });

  apiData.subPages?.forEach((subPage) => {
    const name = subPage.name?.trim();
    const url = subPage.url?.trim();
    if (!name || !url || !subPage.isEnabled) {
      return;
    }
    const path = buildSubPagePath(context.username, url);
    if (!path) {
      return;
    }
    options.push({
      id: buildSubPageMenuOptionId(url),
      label: name,
      url: path,
      isPredefined: true
    });
  });

  return options;
}
